import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SpinningLogo from "../../components/Spinner";
import CustomPageHeader from "../../components/CustomPageHeader";
import { downloadExcel } from "../../utils/DownloadXls";
import moment from "moment";
import { Edit, Trash } from "lucide-react";
import BtnSpinner from "../../components/BtnSpinner";
import Pagination from "../../components/Pagination";
import { Apirequests } from "../../utils/ApiRequests";
import { ErrorTost, SuccessTost } from "../../utils/ToastHelper";
import CustomOffCanvas from "../../components/CustomOffCanvas";
import AddOrEditClass from "./AddOrEditClass";
import AddOrEditUpcomingClass from "./AddOrEditUpcomingClass";

function UpcomingMasterClasses() {
  const [isOffCanvasOpen, setIsOffCanvasOpen] = useState({
    state: false,
    name: "",
  });

  const [classes, setClasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState({ state: false, name: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [refresh, setRefresh] = useState(false);
  const [currentClass, setCurrentClass] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const itemsPerPage = 10;

  const handleRefresh = () => {
    setSearchTerm("");
    setCurrentPage(1);
    setRefresh(!refresh);
  };

  const fetchClasses = async (page = currentPage, search = searchTerm) => {
    setLoading({ state: true, name: "fetchclasses" });

    try {
      let query = `/masterclass/upcoming-master-classes?page=${page}&limit=${itemsPerPage}`;

      if (search) {
        query += `&title=${encodeURIComponent(search)}`;
      }

      const res = await Apirequests(query, "GET", {}, {
        Authorization: `Bearer ${token}`,
      });

      if (res?.success) {
        setClasses(res?.data || []);
        setPagination(
          res?.pagination || {
            total: 0,
            page,
            limit: itemsPerPage,
            totalPages: 1,
          }
        );
      } else {
        ErrorTost(res?.message || "Failed to fetch upcoming master classes");
      }
    } catch (error) {
      console.log(error, "fetch upcoming master classes error");
      ErrorTost("Something went wrong, please try again later.");
    }

    setLoading({ state: false, name: "" });
  };

  const handleSearch = async () => {
    if (!searchTerm) {
      return ErrorTost("Enter a key to search");
    }

    setCurrentPage(1);
    await fetchClasses(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchClasses(newPage, searchTerm);
  };

  const deleteClass = async (id) => {
    if (!window.confirm("Are you sure you want to delete this master class?")) {
      return;
    }

    setDeleteId(id);
    setLoading({ state: true, name: "deleteclass" });

    try {
      const delRes = await Apirequests(
        `upcoming-master-classes/${id}`,
        "DELETE",
        {},
        {
          Authorization: `Bearer ${token}`,
        }
      );

      if (delRes?.success) {
        SuccessTost(delRes?.message);
        fetchClasses(currentPage, searchTerm);
      } else {
        ErrorTost(delRes?.message);
      }
    } catch (error) {
      console.log(error, "delete upcoming master class error");
      ErrorTost("Something went wrong, please try again later.");
    }

    setLoading({ state: false, name: "" });
    setDeleteId(null);
  };

  useEffect(() => {
    fetchClasses(currentPage, searchTerm);
  }, [refresh, token]);

  if (loading.state && loading.name === "fetchclasses") {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[rgba(255,255,255,0.5)]">
        <SpinningLogo />
      </div>
    );
  }

  return (
    <div>
      <CustomPageHeader
        title="Upcoming Master Classes"
        text={searchTerm}
        setText={setSearchTerm}
        handleSearch={handleSearch}
        onClick={() =>
          setIsOffCanvasOpen({ state: true, name: "addclass" })
        }
        btnText="Add Upcoming Master Class"
        handleBackClick={() => navigate("/")}
        handleRefresh={handleRefresh}
        handleDownload={() => downloadExcel(classes, "upcoming-master-classes")}
      />

      <main className="mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-50 text-center">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Course Id
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Course Date
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Link
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Serial Number
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 text-center">
                {classes?.length > 0 ? (
                  classes.map((cl) => (
                    <tr key={cl._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {cl?.course_id}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className="text-indigo-600 cursor-pointer"
                          onClick={() => {
                            setIsOffCanvasOpen({
                              state: true,
                              name: "editclass",
                            });
                            setCurrentClass(cl);
                          }}
                        >
                          {cl?.title}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {cl?.course_date}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {cl?.price}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap max-w-[250px] truncate">
                        {cl?.link}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 rounded-full text-xs ${
                            cl?.status === "Active"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {cl?.status}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {cl?.serial_number}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {moment(cl?.createdAt).format("DD/MM/YYYY - HH:mm")}
                      </td>

                      <td className="px-6 py-4 flex gap-5 justify-center">
                        <button
                          className="flex bg-indigo-400 rounded-lg px-3 py-2 items-center gap-1"
                          onClick={() => {
                            setIsOffCanvasOpen({
                              state: true,
                              name: "editclass",
                            });
                            setCurrentClass(cl);
                          }}
                        >
                          <Edit className="text-white" size={20} />
                          <span className="text-white text-sm">Edit</span>
                        </button>

                        <button
                          className="flex bg-red-200 rounded-lg px-3 py-2 items-center gap-1"
                          onClick={() => deleteClass(cl?._id)}
                        >
                          {loading.state &&
                          loading.name === "deleteclass" &&
                          deleteId === cl?._id ? (
                            <BtnSpinner />
                          ) : (
                            <>
                              <Trash className="text-red-700 text-sm" size={20} />
                              <span className="text-red-700 text-sm">
                                Delete
                              </span>
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      No upcoming master classes found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {pagination?.total > 0 && (
          <div className="flex gap-x-2 items-center justify-between">
            <p>Total: {pagination?.total}</p>

            <Pagination
              page={currentPage}
              totalItems={pagination?.total}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </main>

      <CustomOffCanvas
        open={isOffCanvasOpen.state && isOffCanvasOpen.name === "addclass"}
        close={() => {
          setIsOffCanvasOpen({ state: false, name: "" });
          setCurrentClass(null);
        }}
        content={
          <AddOrEditUpcomingClass
            close={() => {
              setIsOffCanvasOpen({ state: false, name: "" });
              setCurrentClass(null);
            }}
            setRefresh={setRefresh}
            currentClass={null}
          />
        }
      />

      <CustomOffCanvas
        open={isOffCanvasOpen.state && isOffCanvasOpen.name === "editclass"}
        close={() => {
          setIsOffCanvasOpen({ state: false, name: "" });
          setCurrentClass(null);
        }}
        content={
          <AddOrEditUpcomingClass
            close={() => {
              setIsOffCanvasOpen({ state: false, name: "" });
              setCurrentClass(null);
            }}
            setRefresh={setRefresh}
            currentClass={currentClass}
          />
        }
      />
    </div>
  );
}

export default UpcomingMasterClasses;